import { apiFetch } from './client'

export const MINTS = {
  SOL: 'So11111111111111111111111111111111111111112',
  USDC: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  jupSOL: 'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v',
} as const

export const DECIMALS: Record<string, number> = {
  [MINTS.SOL]: 9,
  [MINTS.USDC]: 6,
  [MINTS.jupSOL]: 9,
}

export const SYMBOLS: Record<string, string> = {
  [MINTS.SOL]: 'SOL',
  [MINTS.USDC]: 'USDC',
  [MINTS.jupSOL]: 'jupSOL',
}

export interface JupiterQuote {
  inputMint: string
  outputMint: string
  inAmount: string
  outAmount: string
  otherAmountThreshold: string
  swapMode: string
  slippageBps: number
  priceImpactPct: string
  routePlan: unknown
  [k: string]: unknown
}

export interface JupiterSwapResponse {
  swapTransaction: string
  lastValidBlockHeight: number
  prioritizationFeeLamports?: number
}

/** GET /api/jupiter/quote */
export async function jupiterQuote(params: {
  inputMint: string
  outputMint: string
  amount: string | number
  slippageBps?: number
}): Promise<JupiterQuote> {
  const q = new URLSearchParams({
    inputMint: params.inputMint,
    outputMint: params.outputMint,
    amount: String(params.amount),
    slippageBps: String(params.slippageBps ?? 50),
  })
  return apiFetch<JupiterQuote>(`/api/jupiter/quote?${q.toString()}`)
}

/** POST /api/jupiter/swap — returns base64 serialized VersionedTransaction */
export async function jupiterSwap(params: {
  userPublicKey: string
  quoteResponse: JupiterQuote
  wrapAndUnwrapSol?: boolean
  prioritizationFeeLamports?: number | { priorityLevelWithMaxLamports: { maxLamports: number; priorityLevel: 'low' | 'medium' | 'high' | 'veryHigh' } }
}): Promise<JupiterSwapResponse> {
  const body = {
    userPublicKey: params.userPublicKey,
    quoteResponse: params.quoteResponse,
    wrapAndUnwrapSol: params.wrapAndUnwrapSol ?? true,
    dynamicComputeUnitLimit: true,
    prioritizationFeeLamports: params.prioritizationFeeLamports ?? {
      priorityLevelWithMaxLamports: { maxLamports: 1_000_000, priorityLevel: 'medium' },
    },
  }
  return apiFetch<JupiterSwapResponse>('/api/jupiter/swap', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export interface JupLendToken {
  asset?: { address?: string; symbol?: string; decimals?: number }
  symbol?: string
  assetAddress?: string
  decimals?: number
  totalRate?: string
}

export async function fetchJupLendTokens(): Promise<JupLendToken[]> {
  return apiFetch<JupLendToken[]>('/api/jupiter/lend/tokens').catch(() => [])
}

export interface JupLendPosition {
  token?: { assetAddress?: string; asset?: { symbol?: string; decimals?: number }; symbol?: string; decimals?: number }
  underlyingAssets?: string
}

export async function fetchJupLendPositions(wallet: string): Promise<JupLendPosition[]> {
  return apiFetch<JupLendPosition[]>(
    `/api/jupiter/lend/positions?users=${encodeURIComponent(wallet)}`,
  ).catch(() => [])
}

export interface JupBorrowPortfolio {
  elements?: Array<{
    type?: string
    data?: {
      link?: string
      suppliedAssets?: Array<{ data?: { address?: string; symbol?: string; amount?: number; yields?: Array<{ apy?: number }> } }>
      borrowedAssets?: Array<{ data?: { address?: string; symbol?: string; amount?: number } }>
      borrowedYields?: Array<Array<{ apr?: number }>>
    }
  }>
  tokenInfo?: { solana?: Record<string, { symbol?: string; decimals?: number }> }
}

export async function fetchJupBorrowPortfolio(wallet: string): Promise<JupBorrowPortfolio | null> {
  return apiFetch<JupBorrowPortfolio>(
    `/api/jupiter/lend/portfolio-positions?user=${encodeURIComponent(wallet)}`,
  ).catch(() => null)
}

// ---------- Jupiter Perps (hedge) ----------

export interface JupPerpsPosition {
  positionPubkey: string
  side: 'long' | 'short'
  asset: string
  sizeUsd: string
  collateralUsd: string
  entryPrice: string
  markPrice: string
  liquidationPrice: string
  pnlUsd: string
  leverage: string
  [k: string]: unknown
}

export interface JupPerpsMarketStats {
  asset: string
  price: string
  fundingRate: string
  openInterest?: string
  [k: string]: unknown
}

export interface JupPerpsTxResponse {
  /** Base64-encoded VersionedTransaction */
  transaction?: string
  /** Some endpoints return the already-partially-signed tx under a different key */
  serializedTransaction?: string
  lastValidBlockHeight?: number
  [k: string]: unknown
}

export interface JupPerpsIncreaseRequest {
  /** 'SOL', 'ETH', 'BTC' etc. */
  asset: string
  side: 'long' | 'short'
  /** Mint of the collateral token (USDC mint for stable-collateral short) */
  inputToken: string
  /** Raw integer string (e.g. USDC amount × 10^6) */
  inputTokenAmount: string
  leverage: number
  maxSlippageBps?: number
  /** Signer wallet pubkey */
  owner: string
}

export interface JupPerpsDecreaseRequest {
  positionPubkey: string
  owner: string
  /** USD notional to close (omit + set `entirePosition:true` to close fully) */
  sizeUsdDelta?: string
  entirePosition?: boolean
  receiveToken?: string
  maxSlippageBps?: number
}

export async function fetchJupPerpsPositions(wallet: string): Promise<JupPerpsPosition[]> {
  return apiFetch<{ positions?: JupPerpsPosition[] } | JupPerpsPosition[]>(
    `/api/jupiter/perps/positions?wallet=${encodeURIComponent(wallet)}`,
  )
    .then(r => Array.isArray(r) ? r : (r?.positions ?? []))
    .catch(() => [])
}

export async function fetchJupPerpsMarketStats(): Promise<JupPerpsMarketStats[]> {
  return apiFetch<{ stats?: JupPerpsMarketStats[] } | JupPerpsMarketStats[]>(
    '/api/jupiter/perps/market-stats',
  )
    .then(r => Array.isArray(r) ? r : (r?.stats ?? []))
    .catch(() => [])
}

export async function jupPerpsIncrease(req: JupPerpsIncreaseRequest): Promise<JupPerpsTxResponse> {
  return apiFetch<JupPerpsTxResponse>('/api/jupiter/perps/positions/increase', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

export async function jupPerpsDecrease(req: JupPerpsDecreaseRequest): Promise<JupPerpsTxResponse> {
  return apiFetch<JupPerpsTxResponse>('/api/jupiter/perps/positions/decrease', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}
